import { Injectable } from '@angular/core';
import { RequestConfigImplementation, REQUEST_METHODS, HTTPWrapperContext, XHR_STRATEGIES, AxwayTokenFetcherService, HEADER_CONSTANTS } from '@ptl/xhr';
//import * as moment from 'moment';
import moment from 'moment';
import { Brand, BrandingConfigurationService, brandValues } from '@ptl/brand';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { HttpHeaders } from '@angular/common/http';
import { DateService } from 'src/app/shared/services/date.service';
import { CCRGetResponse, SepLifeEvent } from '../types/qualifying-life-event.response.interface';
import { SEP_CIRCUMSTANCE_ID, SpecialEnrollmentCircumstance } from 'src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface';
import { API_ENDPOINTS } from '../../../../shared/constants/api-endpoints.constant';

export interface ConsumerEffectiveDateRequestPayload {
  baseMessage: ConsumerEffectiveDateBaseMessage;
  payload: ConsumerEffectiveDatePayloadMessage;
}
export interface ConsumerEffectiveDateBaseMessage {
  additionalInfo: null;
  headerVersion: string;
  payloadVersion: string;
}
export interface ConsumerEffectiveDatePayloadMessage{
    effectiveDateValue: string;
    isOffSep: boolean;
    offExchangeIndicator: string;
    regionCd: string;
    options: ConsumerEffectiveDatePayloadOptions;
    sepLifeEvent: SepLifeEvent | undefined;
}

export interface ConsumerEffectiveDatePayloadOptions {
  isDental: boolean;
  isBeb: boolean;
  isIchra?: boolean;
  isQsehra?: boolean;
}

enum CONSUMER_EFFECTIVE_DATE_PAYLOAD_OPTION {
  IS_ICHRA = "isichra",
  IS_QSEHRA = "isqsehra"
}
export interface ConsumerEffectiveDate {
  baseMessage?: any;
  payload: ConsumerEffectiveDatePayload;
}

export interface ConsumerEffectiveDatePayload {
  consumerEffectiveDateSingle: {
    displayEffDate: string;
  }[];
  calculationMetadata?: CalculationMetadata;  // capital C to match API response
}

export interface EffectiveDateRetrievalOptions {
    isSep: boolean;
    region: string;
    offExchangeIndicator: "true" | "false";
    isDental: boolean;
    isBeb: boolean;
    sepLifeEvent?: SepLifeEvent;
}

export interface CalculationMetadata {
    isLifeEventFuture?: boolean,
    sepEffectiveDateCalculated?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class FamilyFormService {

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private brandingConfigurationService: BrandingConfigurationService,
    private date: DateService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  async getEffectiveDates(config: EffectiveDateRetrievalOptions): Promise<ConsumerEffectiveDate> {

    const payload: ConsumerEffectiveDateRequestPayload = {
      baseMessage: {
        additionalInfo: null,
        headerVersion: '1.0',
        payloadVersion: '2.0'
      },
      payload: {
        effectiveDateValue: this.date.getTodayAsMoment().format(),
        isOffSep: config.isSep,
        offExchangeIndicator: config.offExchangeIndicator,
        regionCd: config.region,       
        sepLifeEvent : config.sepLifeEvent,
        options: (() => {
          const options: ConsumerEffectiveDatePayloadOptions = {
            isDental: config.isDental,
            isBeb: config.isBeb
          };

          const payloadStates = this.reducerUtilityService.getPayloadStates();
          const specialEnrollmentState: SpecialEnrollmentCircumstance = payloadStates['special-enrollment-circumstance'];
      
          /** If ICHRA or QSEHRA app, attach the relevant flags */
          let sepLifeId = specialEnrollmentState?.lifeEvent?.lifeEventId;
          if (sepLifeId === SEP_CIRCUMSTANCE_ID.ICHRA || sepLifeId === SEP_CIRCUMSTANCE_ID.QSEHRA) {
            options[CONSUMER_EFFECTIVE_DATE_PAYLOAD_OPTION.IS_ICHRA] = sepLifeId === SEP_CIRCUMSTANCE_ID.ICHRA;
            options[CONSUMER_EFFECTIVE_DATE_PAYLOAD_OPTION.IS_QSEHRA] = sepLifeId === SEP_CIRCUMSTANCE_ID.QSEHRA;
          }
          
          return options;
        })()
      }
    };
    
    /** DD Value */
    //const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    //const matchedBrand: Brand = brandValues.filter(brand => zipCountyState.county.ubk === brand.ubk)[0];
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      POST_OR_PUT_PAYLOAD: payload,
      url: `/sxesvc/api/v1/consumer/consumereffectivedate`,
      headers: new HttpHeaders({
        ddToken: this.brandingConfigurationService.getCurrentBrand().ddToken_encoded()
      })
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<{ payload: { consumerEffectiveDateSingle: { displayEffDate: string }[] } }>(requestConfig);
  }

  getOpenEnrollmentRules(qualifyLifeEventId?: any): Promise<CCRGetResponse> {
    
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const matchedBrand: Brand = brandValues.filter(brand => zipCountyState.county.ubk === brand.ubk)[0];
    const bpdId = matchedBrand.ddToken;
    const portalTypeId = 2;
    /**
     * portalTypeId = 2: sales ?=SEP&lifeEventId=ICHRA/QSEHRA
     * portalTypeId = 1: employer 
     **/
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.ENROLLMENT.SEP_ENROLLMENT_RULE, { enrollmentType: 'SEP', ...(qualifyLifeEventId != null && { lifeEventId: qualifyLifeEventId })}),
      strategyConfigs: { forceModernUrl: true },
      headers: new HttpHeaders({
        ddToken: this.brandingConfigurationService.getCurrentBrand().ddToken_encoded()
      })
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  postFile(fileToUpload: File) {
    // formData.append('fileKey', fileToUpload, fileToUpload.name);
    if (fileToUpload) {
      const payload = new FormData();
      payload.append(`file`, fileToUpload);
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/cmcrst/x-services/public/uploadSepDocument',
        withCredentials: false,
        POST_OR_PUT_PAYLOAD: payload,
        contentType: 'force_undefined',
      });
      this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
      return this.httpClient.makeRequest(requestConfig);
    }
  }
}
